import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {  User, Deployments } from 'types';
import { canSignUpForDeployment, shouldHideIntakeDeployment } from 'util/user';
import useIntakeDeploymentOptions from 'hooks/api/useIntakeDeploymentOptions';

import { setIntakeDeploymentId } from 'redux/actions/intake';
import { closePopUp } from 'redux/store/slices/eligibilitySlice';

import Button from '@mui/material/Button';
import { Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';

const HEADING_TITLE = 'Select a deployment';
const HEADING_DESCRIPTION = 'Please select the insurance or provider group that this patient\'s Membership will be a part of';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        '& button': {
            padding: '16px 48px',
            width: '100%'
        }
    },
    deploymentButton: {
        border: '1px solid #E3E3FA',
        borderRadius: '16px',
        boxShadow: '0px 8px 16px 4px #0000000D',
        fontSize: '18px',
        padding: '20px',
        justifyContent: 'space-between',
        textTransform: 'none',
        '& i': {
            color: '#999DFF'
        },
        '& span': {
            color: '#1F2152',
            fontWeight: 600
        }
    },
    deploymentButtonDisabled: {
        boxShadow: 'none',
        border: '1px solid #E3E3FA80',
        cursor: 'not-allowed',
        '& i': {
            color: '#A6A6BF'
        },
        '& span': {
            color: '#A6A6BF',
            fontWeight: 600
        }
    },
    deploymentSelectorContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        margin: '24px 0'
    },
    heading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        paddingBottom: '24px',
        '& h2': {
            color: '#062D7A',
            margin: 0
        },
        '& span': {
            color: '#5A6A81',
            fontWeight: 300,
            maxWidth: '75%',
            textAlign: 'center'
        }
    },
    disabledText: {
        border: '1px solid #A6A6BF40',
        backgroundColor: '#E3E3FA80',
        borderRadius: '100px',
        padding: '4px 12px',
        color: '#A6A6BF80',
        fontWeight: 500,
        fontSize: '14px',
        maxWidth: '50%',
        textAlign: 'center',
        textTransform: 'uppercase'
    },
    skeletonButton: {
        borderRadius: '16px'
    }
}));

type DeploymentSelectorProps = {
    setConfirmedDeployment: (deployment: null | Deployments) => void;
    user: User;
};

const DeploymentSelector: React.FC<DeploymentSelectorProps> = ({
    setConfirmedDeployment,
    user,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const { intakeDeploymentOptions, isFetchingIntakeDeploymentOptions } = useIntakeDeploymentOptions(user);
    
    useEffect(() => {
        //if there is only one deployment and it can accept sign ups, automatically select it
        if (intakeDeploymentOptions.length !== 1) return;
        const firstDeployment = intakeDeploymentOptions[0];
        if (!canSignUpForDeployment(firstDeployment).canSignUp) return;
        handleDeploymentClick(firstDeployment.id, firstDeployment.is_eligibility_check_required);
    }, [intakeDeploymentOptions]);

    // Pre-intake list. Open/closed comes from can_sign_up.
    // Grandfathered deployments and short_reasoning containing "commitment" stay hidden.
    const deployments = intakeDeploymentOptions.filter(
        (deployment) => !shouldHideIntakeDeployment(deployment)
    );

    const handleDeploymentClick = (
        deploymentId: number,
        requiresEligibilityCheck: boolean
    ) => {
        dispatch(setIntakeDeploymentId(deploymentId));
        setConfirmedDeployment(intakeDeploymentOptions.find(deployment => deployment.id === deploymentId) || null);

        if (!requiresEligibilityCheck) {
            dispatch(closePopUp());
            history.push('/intake');
        }
    };

    if (isFetchingIntakeDeploymentOptions) {
        return (
            <div className={classes.deploymentSelectorContainer}>
                <div className={classes.heading}>
                    <Skeleton variant="text" height={24} width={100} />
                    <Skeleton variant="text" height={16} width={200} />
                </div>
                {[1, 2, 3].map((index) => (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        height={60}
                        animation="wave"
                        className={classes.skeletonButton}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={classes.deploymentSelectorContainer}>
            <div className={classes.heading}>
                <h2>{HEADING_TITLE}</h2>
                <span>{HEADING_DESCRIPTION}</span>
            </div>
            {deployments.map((deployment) => {
                const signUpAvailabilityResult = canSignUpForDeployment(deployment);
                return (
                    <Button
                        key={deployment.id}
                        aria-label={
                            !signUpAvailabilityResult.canSignUp ? `${deployment.name} is not available: ${signUpAvailabilityResult.errorMessage?.longReason || signUpAvailabilityResult.errorMessage?.shortReason || ''}` : `Select ${deployment.name} and navigate to member registration`
                        }
                        className={`${classes.deploymentButton} ${!signUpAvailabilityResult.canSignUp ? classes.deploymentButtonDisabled : ''}`}
                        disabled={!signUpAvailabilityResult.canSignUp}
                        onClick={() =>
                            signUpAvailabilityResult.canSignUp && handleDeploymentClick(
                                deployment.id,
                                deployment.is_eligibility_check_required
                            )
                        }
                    >
                        <span>{deployment.name}</span>
                        {signUpAvailabilityResult.canSignUp && (
                            <i className="far fa-arrow-right" />
                        )}
                        {!signUpAvailabilityResult.canSignUp && (signUpAvailabilityResult.errorMessage?.shortReason || signUpAvailabilityResult.errorMessage?.longReason) && (
                            <div className={classes.disabledText}>{signUpAvailabilityResult.errorMessage?.shortReason || signUpAvailabilityResult.errorMessage?.longReason}</div>
                        )}
                    </Button>
                );
            })}
        </div>
    );
};

export default DeploymentSelector;
